/**
 * app-toast - Toast notification container component
 * Displays toast notifications with auto-dismiss and manual close
 * Supports success, error, warning, and info variants
 */
import { BaseElement } from '../base-element';
import { toastState, toastActions, Toast, ToastType } from '../../state/toast.state';

export class AppToast extends BaseElement {
  connectedCallback(): void {
    this.updateRender();

    // Watch toast state changes
    this.watch(toastState, () => this.updateRender());
  }

  public dismissToast(id: string): void {
    toastActions.removeToast(id);
  }

  private getTypeStyles(type: ToastType): {
    indicatorClass: string;
    iconClass: string;
    borderClass: string;
    icon: string;
    iconFill: boolean;
  } {
    const styles: Record<ToastType, {
      indicatorClass: string;
      iconClass: string;
      borderClass: string;
      icon: string;
      iconFill: boolean;
    }> = {
      success: {
        indicatorClass: 'bg-success-500',
        iconClass: 'bg-success-50 text-success-700',
        borderClass: 'border-success-500/20',
        icon: 'check_circle',
        iconFill: true,
      },
      error: {
        indicatorClass: 'bg-error-500',
        iconClass: 'bg-error-50 text-error-700',
        borderClass: 'border-error-500/20',
        icon: 'error',
        iconFill: true,
      },
      warning: {
        indicatorClass: 'bg-warning-500',
        iconClass: 'bg-warning-50 text-warning-700',
        borderClass: 'border-warning-500/20',
        icon: 'warning',
        iconFill: false,
      },
      info: {
        indicatorClass: 'bg-info-500',
        iconClass: 'bg-info-50 text-info-700',
        borderClass: 'border-info-500/20',
        icon: 'info',
        iconFill: false,
      },
    };

    return styles[type];
  }

  private renderToast(toast: Toast): string {
    const styles = this.getTypeStyles(toast.type);

    return `
      <div class="toast flex min-h-[72px] overflow-hidden rounded-xl border bg-white shadow-[0_10px_40px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] pointer-events-auto animate-slide-in dark:bg-surface-dark dark:border-white/10 dark:shadow-[0_10px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] ${styles.borderClass}" data-toast-id="${toast.id}" data-toast-type="${toast.type}">
        <div class="h-full w-1.5 flex-shrink-0 ${styles.indicatorClass}"></div>
        <div class="flex min-w-0 flex-1 items-start gap-3 p-4">
          <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${styles.iconClass}">
            <span class="material-symbols-outlined" data-fill="${styles.iconFill}">${styles.icon}</span>
          </div>
          <div class="toast-text flex min-w-0 flex-1 flex-col justify-center">
            <p class="toast-message text-sm font-semibold text-neutral-900 dark:text-white">${this.escapeHtml(toast.message)}</p>
            ${toast.supportId ? `
              <p class="toast-support-id mt-1 text-xs text-neutral-500 dark:text-neutral-400 font-mono">Support ID: ${this.escapeHtml(toast.supportId)}</p>
            ` : ''}
          </div>
          <button class="toast-close flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500 dark:text-neutral-400 dark:hover:bg-white/10" data-toast-dismiss="${toast.id}" aria-label="Dismiss notification">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  private attachEventListeners(): void {
    this.$$('[data-toast-dismiss]').forEach((el) => {
      const id = el.getAttribute('data-toast-dismiss');
      if (!id) return;
      el.addEventListener('click', () => this.dismissToast(id));
    });
  }

  private updateRender(): void {
    const { toasts } = toastState.get();

    this.render(`
      <div class="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none" role="region" aria-label="Notifications" aria-live="polite">
        ${toasts.map(toast => this.renderToast(toast)).join('')}
      </div>
    `);

    this.attachEventListeners();
  }

  // Expose methods for external use
  public showSuccess(message: string, supportId?: string): string {
    return toastActions.success(message, supportId);
  }

  public showError(message: string, supportId?: string): string {
    return toastActions.error(message, supportId);
  }

  public showWarning(message: string, supportId?: string): string {
    return toastActions.warning(message, supportId);
  }

  public showInfo(message: string, supportId?: string): string {
    return toastActions.info(message, supportId);
  }
}

customElements.define('app-toast', AppToast);
